import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../store'
import WebSocketManager from '../../services/websocketManager'
import { ChatMessage } from '../types/chat'
import { setAdvisorChatLoadingMore, setIsGenerating } from '../slices/chatSlice'
import {
  setConnectionStatus,
  setWebSocketError,
} from '../slices/advisorWebsocketSlice'
import { SenderType } from '@/constants/chat'

// Get the websocket manager instance
const wsManager = WebSocketManager.getInstance()

// Advisor websocket thunks
export const connectAdvisorWebSocket = createAsyncThunk<
  void,
  {
    jwtToken: string
    inviteCode: string
    studentId?: string
    phaseId?: string
    impersonatePlayerId?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectAdvisor',
  async (
    { jwtToken, inviteCode, studentId, phaseId, impersonatePlayerId },
    { rejectWithValue }
  ) => {
    try {
      return await wsManager.connectToAdvisorChat(
        jwtToken,
        inviteCode,
        phaseId,
        studentId,
        impersonatePlayerId
      )
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || 'Failed to connect to advisor websocket'
      )
    }
  }
)

export const disconnectAdvisorWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/disconnectAdvisor', async (_, { dispatch }) => {
  try {
    await wsManager.disconnect('advisor')
    dispatch(setConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting advisor WebSocket'
    dispatch(setWebSocketError(errorMessage))
  }
})

export const disconnectAllAdvisorWebSockets = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/disconnectAllAdvisor', async (_, { dispatch, rejectWithValue }) => {
  try {
    await wsManager.disconnectAllAdvisorConnections()
    dispatch(setConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting advisor WebSockets'
    dispatch(setWebSocketError(errorMessage))
    return rejectWithValue(errorMessage)
  }
})

export const sendAdvisorWebSocketMessage = createAsyncThunk<
  void,
  { message: ChatMessage; chatbotId: string[] },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendAdvisorMessage',
  async ({ message, chatbotId }, { rejectWithValue, dispatch }) => {
    if (!message.sendToProfessor && message.sender_type === SenderType.Player) {
      dispatch(setIsGenerating(true))
    }

    const success = wsManager.sendMessage('advisor', {
      message: message.message,
      user: message.user,
      chatbotid: chatbotId,
      sender_type: message.sender_type,
      sendToProfessor: message.sendToProfessor,
      phaseId: message.phaseId,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const stopResponseGeneration = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/stopResponseGeneration', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('advisor', {
    type: 'stop_generation',
  })

  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

// Waiting room websocket thunks
export const connectWaitingRoomWebSocket = createAsyncThunk<
  void,
  { jwtToken: string; roomId: string; inviteCode: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectWaitingRoom',
  async ({ jwtToken, roomId, inviteCode }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToWaitingRoom(jwtToken, roomId, inviteCode)
    } catch (error) {
      return rejectWithValue(
        (error as Error).message ||
          'Failed to connect to waiting room websocket'
      )
    }
  }
)

export const disconnectWaitingRoomWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('websocket/disconnectWaitingRoom', async () => {
  return await wsManager.disconnect('waitingRoom')
})

export const sendAdjudicationMessage = createAsyncThunk<
  void,
  { message: string; decisionId: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendAdjudicationMessage',
  async ({ message, decisionId }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('adjudication', {
      type: 'send_message',
      message,
      decision_id: decisionId,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

// Disconnect all websockets
export const disconnectAllWebSockets = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('websocket/disconnectAll', async () => {
  await wsManager.disconnectAll()
})

export const loadMoreAdvisorMessages = createAsyncThunk<
  void,
  { page: number },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/loadMoreAdvisorMessages',
  async ({ page }, { rejectWithValue, dispatch }) => {
    dispatch(setAdvisorChatLoadingMore(true))

    const success = wsManager.sendMessage('advisor', {
      type: 'load_more_messages',
      page: page,
    })

    if (!success) {
      dispatch(setAdvisorChatLoadingMore(false))
      return rejectWithValue('WebSocket is not connected')
    }
  }
)
